/**
 * CESM OSINT System - Bibliotheque de Prompts OSINT
 * 103 prompts classes par categorie, executables via IA Claude
 *
 * Concept & Developpement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Globe, Shield, Radar, AlertTriangle, 
  TrendingUp, BookOpen, Compass, FlaskConical,
  Users, Play, Loader2, Copy, ChevronDown, ChevronUp,
  History, Satellite, Filter, X, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { ScrollArea } from '@/components/ui/scroll-area';

const CATEGORY_ICONS = {
  google_dorking: Search,
  socmint: Users,
  satellite_imint: Satellite,
  masint: Radar,
  natural_disasters: AlertTriangle,
  geopolitical: Globe,
  cyber_intel: Shield,
  grey_literature: BookOpen,
  crypto_finint: TrendingUp,
  general_osint: Compass,
  scientific_intel: FlaskConical,
};

const CATEGORY_COLORS = {
  google_dorking: 'from-blue-500/20 to-blue-900/10 border-blue-500/40',
  socmint: 'from-purple-500/20 to-purple-900/10 border-purple-500/40',
  satellite_imint: 'from-cyan-500/20 to-cyan-900/10 border-cyan-500/40',
  masint: 'from-green-500/20 to-green-900/10 border-green-500/40',
  natural_disasters: 'from-orange-500/20 to-orange-900/10 border-orange-500/40',
  geopolitical: 'from-red-500/20 to-red-900/10 border-red-500/40',
  cyber_intel: 'from-emerald-500/20 to-emerald-900/10 border-emerald-500/40',
  grey_literature: 'from-amber-500/20 to-amber-900/10 border-amber-500/40',
  crypto_finint: 'from-yellow-500/20 to-yellow-900/10 border-yellow-500/40',
  general_osint: 'from-slate-500/20 to-slate-900/10 border-slate-500/40',
  scientific_intel: 'from-indigo-500/20 to-indigo-900/10 border-indigo-500/40',
};

function PromptCard({ prompt, onExecute, isExecuting }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = CATEGORY_ICONS[prompt.category] || Compass;

  return (
    <div 
      className="bg-black/40 border border-white/10 rounded p-3 hover:border-marine-cyan/40 transition-all"
      data-testid={`prompt-card-${prompt.id}`}
    >
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 text-marine-cyan shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-mono text-white truncate">{prompt.title}</h4>
            <span className="text-[10px] px-1.5 py-0.5 bg-marine-cyan/20 text-marine-cyan rounded font-mono shrink-0">
              #{prompt.id}
            </span>
          </div>
          <p className="text-xs text-white/50 mt-1 font-mono">{prompt.context}</p>
          
          {expanded && (
            <div className="mt-2 p-2 bg-black/60 rounded text-xs text-white/70 font-mono border border-white/5">
              {prompt.prompt}
            </div>
          )}
          
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-[10px] text-white/40 hover:text-white/70 font-mono flex items-center gap-1"
              data-testid={`prompt-expand-${prompt.id}`}
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Masquer' : 'Voir prompt'}
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(prompt.prompt)}
              className="text-[10px] text-white/40 hover:text-white/70 font-mono flex items-center gap-1"
              data-testid={`prompt-copy-${prompt.id}`}
            >
              <Copy className="w-3 h-3" /> Copier
            </button>
            <button
              onClick={() => onExecute(prompt)}
              disabled={isExecuting}
              className="ml-auto text-[10px] px-2 py-1 bg-marine-cyan/20 text-marine-cyan hover:bg-marine-cyan/30 rounded font-mono flex items-center gap-1 disabled:opacity-50"
              data-testid={`prompt-execute-${prompt.id}`}
            >
              {isExecuting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              Executer IA
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AIResponseModal({ response, onClose }) {
  if (!response) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-[#0a0f1a] border border-marine-cyan/30 rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-sm font-mono text-marine-cyan font-bold">{response.prompt_title}</h3>
            <p className="text-[10px] text-white/50 font-mono mt-1">
              {response.source_type} | {new Date(response.timestamp).toLocaleString('fr-FR')}
            </p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="close-ai-response">
            <X className="w-5 h-5" />
          </button>
        </div>
        <ScrollArea className="flex-1 p-4">
          <pre className="text-xs font-mono text-white/80 whitespace-pre-wrap leading-relaxed">
            {response.response}
          </pre>
        </ScrollArea>
        <div className="p-3 border-t border-white/10 flex justify-end">
          <button
            onClick={() => {
              navigator.clipboard.writeText(response.response);
              toast.success('Reponse copiee');
            }}
            className="px-3 py-1.5 bg-marine-cyan/20 text-marine-cyan text-xs font-mono rounded hover:bg-marine-cyan/30 flex items-center gap-1"
            data-testid="copy-ai-response"
          >
            <Copy className="w-3 h-3" /> Copier la reponse
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OsintPromptsPanel({ token }) {
  const [categories, setCategories] = useState({});
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executingId, setExecutingId] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);
  const [userContext, setUserContext] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    try {
      const [catRes, promptsRes] = await Promise.all([
        axios.get(`${API}/osint-prompts/categories`, authHeaders),
        axios.get(`${API}/osint-prompts/all`, authHeaders),
      ]);
      setCategories(catRes.data.categories || {});
      setPrompts(promptsRes.data.prompts || []);
      setFilteredPrompts(promptsRes.data.prompts || []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    let result = prompts;
    if (selectedCategory) {
      result = result.filter(p => p.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.title.toLowerCase().includes(q) || 
        p.prompt.toLowerCase().includes(q) ||
        (p.context || '').toLowerCase().includes(q)
      );
    }
    setFilteredPrompts(result);
  }, [selectedCategory, searchQuery, prompts]);

  const executePrompt = async (prompt) => {
    setIsExecuting(true);
    setExecutingId(prompt.id);
    toast.info(`Execution du prompt #${prompt.id}...`);
    try {
      const res = await axios.post(`${API}/osint-prompts/execute`, {
        prompt_id: prompt.id,
        user_context: userContext,
      }, authHeaders);
      if (res.data.status === 'success') {
        setAiResponse(res.data);
        toast.success('Analyse IA terminee');
      } else {
        toast.error(res.data.message || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur execution: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsExecuting(false);
      setExecutingId(null);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/osint-prompts/history?limit=10`, authHeaders);
      setHistory(res.data.history || []);
      setShowHistory(true);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  return (
    <div className="space-y-4" data-testid="osint-prompts-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-mono text-marine-cyan font-bold flex items-center gap-2">
            <Zap className="w-5 h-5" />
            OSINT PROMPT LIBRARY
          </h2>
          <p className="text-xs text-white/50 font-mono mt-1">
            103 prompts operationnels | Source: Awesome-AI-OSINT | Execution via Claude Sonnet
          </p>
        </div>
        <button
          onClick={fetchHistory}
          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded text-xs font-mono text-white/70 hover:text-white flex items-center gap-1"
          data-testid="prompt-history-btn"
        >
          <History className="w-3.5 h-3.5" /> Historique
        </button>
      </div>

      {/* Search + Context */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Rechercher un prompt..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-2 bg-black/40 border border-white/10 rounded text-sm font-mono text-white placeholder:text-white/30 focus:border-marine-cyan/50 focus:outline-none"
            data-testid="prompt-search-input"
          />
        </div>
        <input
          type="text"
          placeholder="Contexte additionnel (optionnel)..."
          value={userContext}
          onChange={e => setUserContext(e.target.value)}
          className="flex-1 px-3 py-2 bg-black/40 border border-white/10 rounded text-sm font-mono text-white placeholder:text-white/30 focus:border-marine-cyan/50 focus:outline-none"
          data-testid="prompt-context-input"
        />
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-2.5 py-1 rounded text-[11px] font-mono border transition-all ${
            !selectedCategory 
              ? 'bg-marine-cyan/20 text-marine-cyan border-marine-cyan/50' 
              : 'bg-black/30 text-white/50 border-white/10 hover:text-white/70'
          }`}
          data-testid="filter-all"
        >
          Tous ({prompts.length})
        </button>
        {Object.entries(categories).map(([catId, cat]) => {
          const Icon = CATEGORY_ICONS[catId] || Compass;
          return (
            <button
              key={catId}
              onClick={() => setSelectedCategory(selectedCategory === catId ? null : catId)}
              className={`px-2.5 py-1 rounded text-[11px] font-mono border transition-all flex items-center gap-1 ${
                selectedCategory === catId
                  ? 'bg-marine-cyan/20 text-marine-cyan border-marine-cyan/50'
                  : 'bg-black/30 text-white/50 border-white/10 hover:text-white/70'
              }`}
              data-testid={`filter-${catId}`}
            >
              <Icon className="w-3 h-3" />
              {cat.label} ({cat.count})
            </button>
          );
        })}
      </div>

      {/* Prompts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto pr-1">
        {filteredPrompts.map(prompt => (
          <PromptCard
            key={prompt.id}
            prompt={prompt}
            onExecute={executePrompt}
            isExecuting={isExecuting && executingId === prompt.id}
          />
        ))}
        {filteredPrompts.length === 0 && (
          <div className="col-span-2 text-center py-8 text-white/30 font-mono text-sm">
            Aucun prompt ne correspond a votre recherche
          </div>
        )}
      </div>

      {/* AI Response Modal */}
      <AIResponseModal response={aiResponse} onClose={() => setAiResponse(null)} />

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowHistory(false)}>
          <div 
            className="bg-[#0a0f1a] border border-marine-cyan/30 rounded-lg max-w-2xl w-full max-h-[70vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-sm font-mono text-marine-cyan font-bold">Historique des Executions</h3>
              <button onClick={() => setShowHistory(false)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ScrollArea className="flex-1 p-4">
              {history.length === 0 ? (
                <p className="text-center text-white/30 font-mono text-sm py-8">Aucune execution enregistree</p>
              ) : (
                <div className="space-y-3">
                  {history.map((h, i) => (
                    <div key={i} className="bg-black/40 border border-white/10 rounded p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono text-white">{h.prompt_title}</span>
                        <span className="text-[10px] text-white/40 font-mono">
                          {new Date(h.timestamp).toLocaleString('fr-FR')}
                        </span>
                      </div>
                      <p className="text-xs text-white/50 font-mono mt-1 line-clamp-2">{h.response?.substring(0, 150)}...</p>
                      <button
                        onClick={() => { setAiResponse(h); setShowHistory(false); }}
                        className="text-[10px] text-marine-cyan font-mono mt-1 hover:underline"
                      >
                        Voir la reponse complete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      )}
    </div>
  );
}
